import { buildMentionQueue, MentionSelection } from '@/lib/utils/mention';
import * as React from 'react';

interface MentionHighlighterProps {
  text: string;
  mentions: MentionSelection[];
}

/**
 * Renders a highlighted view of mentions within a block of text.
 * - Highlights as: @<span>Name</span>
 * - Only highlights as many occurrences per name as provided in mentions
 */
export function MentionHighlighter({
  text,
  mentions,
}: MentionHighlighterProps): JSX.Element | null {
  if (!text) return null;
  const queue = buildMentionQueue(mentions);
  const distinctNames = Array.from(queue.keys());
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let buffer = '';

  const flushBuffer = (): void => {
    if (buffer.length > 0) {
      nodes.push(<span key={`t-${nodes.length}`}>{buffer}</span>);
      buffer = '';
    }
  };

  while (i < text.length) {
    if (text[i] === '@') {
      let matched = false;
      for (let n = 0; n < distinctNames.length; n += 1) {
        const name = distinctNames[n];
        const candidate = '@' + name;
        if (text.startsWith(candidate, i)) {
          const afterIdx = i + candidate.length;
          const boundary = afterIdx === text.length || /\s/.test(text[afterIdx]);
          const ids = queue.get(name) ?? [];
          if (boundary && ids.length > 0) {
            // consume one occurrence
            ids.shift();
            queue.set(name, ids);
            flushBuffer();
            nodes.push(
              <span key={`m-${nodes.length}`} className="rounded bg-blue-100 text-white">
                @{name}
              </span>,
            );
            i = afterIdx;
            matched = true;
            break;
          }
        }
      }
      if (matched) continue;
    }
    buffer += text[i];
    i += 1;
  }
  flushBuffer();

  return <>{nodes}</>;
}

export default MentionHighlighter;
